import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../AuthContext";
import { LOCALES } from "../i18n";
import api, { errMsg } from "../api";
import {
    Clock, Play, Square, Pause as PauseIcon, MapPin, Home, Briefcase, AlertTriangle,
    CheckCircle2, XCircle, RefreshCw, Plus, Settings as SettingsIcon, Download, Edit3, Trash2, Loader2, Users, BarChart3, TrendingUp, TrendingDown, CalendarDays
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";

const formatDuration = (mins) => {
    if (!mins && mins !== 0) return "—";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const formatDateTime = (iso, lang) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString(LOCALES[lang] || "de-DE");
};

export default function TimetrackPage() {
    const { user, lang } = useAuth();
    const isPriv = user?.role === "admin" || user?.role === "master";
    const [consent, setConsent] = useState(null);
    const [tab, setTab] = useState("track");

    useEffect(() => {
        api.get("/timetrack/consent").then((r) => setConsent(r.data)).catch(() => {});
    }, []);

    if (!consent) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>;
    if (!consent.consented) return <ConsentScreen onAccept={(c) => setConsent(c)} lang={lang} />;

    return (
        <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="timetrack-page">
            <header className="mb-4 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-display font-bold flex items-center gap-2"><Clock className="w-7 h-7" /> Zeiterfassung</h1>
                    <p className="text-xs text-neutral-500 mt-0.5">{lang === "de" ? "Ein- und Ausstechen mit GPS-Standort" : "Clock in/out with GPS"}</p>
                </div>
            </header>
            <Tabs value={tab} onValueChange={setTab}>
                <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                <TabsList>
                    <TabsTrigger value="track" data-testid="tt-tab-track"><Play className="w-3.5 h-3.5 mr-1.5" />{lang === "de" ? "Stechuhr" : "Time clock"}</TabsTrigger>
                    <TabsTrigger value="entries" data-testid="tt-tab-entries"><Clock className="w-3.5 h-3.5 mr-1.5" />{lang === "de" ? "Meine Einträge" : "My entries"}</TabsTrigger>
                    <TabsTrigger value="stats" data-testid="tt-tab-stats"><BarChart3 className="w-3.5 h-3.5 mr-1.5" />{lang === "de" ? "Statistik" : "Statistics"}</TabsTrigger>
                    {isPriv && <TabsTrigger value="admin" data-testid="tt-tab-admin"><SettingsIcon className="w-3.5 h-3.5 mr-1.5" />{lang === "de" ? "Verwaltung" : "Admin"}</TabsTrigger>}
                </TabsList>
                </div>
                <TabsContent value="track"><TrackingPanel lang={lang} /></TabsContent>
                <TabsContent value="entries"><EntriesPanel lang={lang} mode="me" /></TabsContent>
                <TabsContent value="stats"><StatsPanel lang={lang} isPriv={isPriv} userId={user?.id} /></TabsContent>
                {isPriv && <TabsContent value="admin"><AdminPanel lang={lang} /></TabsContent>}
            </Tabs>
        </div>
    );
}

// ============= Consent Screen =============
function ConsentScreen({ onAccept, lang }) {
    const [busy, setBusy] = useState(false);
    const accept = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/timetrack/consent", { consented: true });
            toast.success(lang === "de" ? "Zustimmung erteilt" : "Consent granted");
            onAccept(data);
        } catch (e) { toast.error(errMsg(e, "Error")); } finally { setBusy(false); }
    };
    return (
        <div className="min-h-[80vh] flex items-center justify-center p-6">
            <div className="max-w-lg bg-white rounded-2xl border border-neutral-200 p-6 shadow-sm" data-testid="tt-consent">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                    <MapPin className="w-6 h-6 text-blue-700" />
                </div>
                <h2 className="text-xl font-display font-bold mb-2">{lang === "de" ? "GPS-Zustimmung erforderlich" : "GPS consent required"}</h2>
                <p className="text-sm text-neutral-700 leading-relaxed mb-4">
                    {lang === "de" ? "Für die Zeiterfassung benötigen wir Zugriff auf deinen Standort. Damit wird:" : "We need access to your location. With this:"}
                </p>
                <ul className="text-xs text-neutral-700 space-y-1.5 mb-5 list-disc list-inside">
                    <li>{lang === "de" ? "Beim Ein-/Ausstechen deine GPS-Position erfasst" : "On clock-in/out, your GPS is captured"}</li>
                    <li>{lang === "de" ? "Während der Arbeit periodisch geprüft, ob du im 1km-Radius bist" : "During work, periodic checks if you're within the 1km radius"}</li>
                    <li>{lang === "de" ? "Bei Verlassen des Radius wird eine Notiz erfasst und Admin benachrichtigt" : "Leaving the radius is logged and admins are notified"}</li>
                    <li>{lang === "de" ? "Die Zustimmung kannst du jederzeit in den Einstellungen widerrufen" : "You can revoke consent anytime in settings"}</li>
                </ul>
                <Button onClick={accept} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="tt-consent-accept">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                    {lang === "de" ? "Ich stimme zu" : "I accept"}
                </Button>
                <p className="text-[10px] text-neutral-400 text-center mt-3">{lang === "de" ? "Ohne Zustimmung kann jeder Eintrag nur manuell mit Admin-Genehmigung erfasst werden." : "Without consent, every entry requires admin approval."}</p>
            </div>
        </div>
    );
}

// ============= Tracking Panel =============
function TrackingPanel({ lang }) {
    const [active, setActive] = useState(null);
    const [options, setOptions] = useState({ offices: [], home_office: null, allow_outside: true });
    const [chosen, setChosen] = useState("");
    const [gps, setGps] = useState(null);
    const [gpsErr, setGpsErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [elapsed, setElapsed] = useState("");

    const reload = useCallback(async () => {
        const [a, o] = await Promise.all([
            api.get("/timetrack/active").then((r) => r.data).catch(() => null),
            api.get("/timetrack/me/options").then((r) => r.data).catch(() => ({})),
        ]);
        setActive(a);
        setOptions(o || {});
    }, []);

    useEffect(() => { reload(); }, [reload]);

    // Live timer
    useEffect(() => {
        if (!active?.started_at) { setElapsed(""); return; }
        const updater = () => {
            const start = new Date(active.started_at).getTime();
            const breaks = active.breaks || [];
            let pause = 0;
            for (const b of breaks) {
                const bs = new Date(b.started_at).getTime();
                const be = b.ended_at ? new Date(b.ended_at).getTime() : Date.now();
                pause += be - bs;
            }
            const totalMs = Date.now() - start - pause;
            const min = Math.floor(totalMs / 60000);
            const sec = Math.floor((totalMs % 60000) / 1000);
            const h = Math.floor(min / 60);
            const m = min % 60;
            setElapsed(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`);
        };
        updater();
        const id = setInterval(updater, 1000);
        return () => clearInterval(id);
    }, [active]);

    // Periodic GPS ping while clocked in
    useEffect(() => {
        if (!active || active.location_type === "outside") return;
        const ping = () => {
            if (!navigator.geolocation) return;
            navigator.geolocation.getCurrentPosition(
                async (p) => {
                    try {
                        const { data } = await api.post("/timetrack/gps-ping", { lat: p.coords.latitude, lng: p.coords.longitude });
                        if (data.violation) {
                            toast.warning(`${lang === "de" ? "Du hast den Standort verlassen" : "You left the location"} (${Math.round(data.distance_m)}m)`);
                        }
                    } catch (e) { /* ignore */ }
                },
                () => {},
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
            );
        };
        ping();
        const id = setInterval(ping, 5 * 60 * 1000); // every 5 minutes
        return () => clearInterval(id);
    }, [active, lang]);

    const requestGps = () => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) return reject(new Error("GPS nicht verfügbar"));
            navigator.geolocation.getCurrentPosition(
                (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
                (err) => reject(err),
                { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
            );
        });
    };

    const clockIn = async () => {
        if (!chosen) { toast.error(lang === "de" ? "Bitte Standort wählen" : "Please pick a location"); return; }
        setBusy(true);
        let payload = { location_type: "outside" };
        if (chosen === "outside") { payload = { location_type: "outside" }; }
        else if (chosen === "home_office") { payload = { location_type: "home_office" }; }
        else { payload = { location_type: "office", location_id: chosen }; }
        if (chosen !== "outside") {
            try {
                const g = await requestGps();
                setGps(g); setGpsErr("");
                payload.lat = g.lat; payload.lng = g.lng;
            } catch (e) {
                setGpsErr(e.message || "GPS-Fehler");
                toast.warning(lang === "de" ? "GPS nicht verfügbar — Admin-Genehmigung wird angefordert" : "No GPS — admin approval requested");
            }
        }
        try {
            const { data } = await api.post("/timetrack/clock-in", payload);
            if (data.requires_approval) toast.warning(lang === "de" ? "Eingestochen — wartet auf Genehmigung" : "Clocked in — awaiting approval");
            else toast.success(lang === "de" ? "Eingestochen" : "Clocked in");
            setActive(data);
            setChosen("");
        } catch (e) { toast.error(errMsg(e, "Clock-in failed")); } finally { setBusy(false); }
    };

    const clockOut = async () => {
        setBusy(true);
        let lat = null, lng = null;
        try {
            const g = await requestGps();
            lat = g.lat; lng = g.lng;
        } catch (e) { /* OK to clock out without GPS */ }
        try {
            const { data } = await api.post("/timetrack/clock-out", { lat, lng });
            toast.success(`${lang === "de" ? "Ausgestochen" : "Clocked out"} — ${formatDuration(data.worked_minutes)}`);
            setActive(null);
        } catch (e) { toast.error(errMsg(e, "Clock-out failed")); } finally { setBusy(false); }
    };

    const breakToggle = async () => {
        const breaks = active?.breaks || [];
        const onBreak = breaks.length > 0 && !breaks[breaks.length - 1].ended_at;
        setBusy(true);
        try {
            const ep = onBreak ? "/timetrack/break/end" : "/timetrack/break/start";
            await api.post(ep);
            toast.success(onBreak ? (lang === "de" ? "Pause beendet" : "Break ended") : (lang === "de" ? "Pause gestartet" : "Break started"));
            reload();
        } catch (e) { toast.error(errMsg(e, "Error")); } finally { setBusy(false); }
    };

    if (active) {
        const breaks = active.breaks || [];
        const onBreak = breaks.length > 0 && !breaks[breaks.length - 1].ended_at;
        const statusBadge = active.approval_status === "pending"
            ? <Badge className="bg-amber-100 text-amber-800 border-amber-300">{lang === "de" ? "Wartet auf Genehmigung" : "Awaiting approval"}</Badge>
            : <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">{lang === "de" ? "Genehmigt" : "Approved"}</Badge>;
        return (
            <div className="grid md:grid-cols-3 gap-4 mt-4">
                <div className={`md:col-span-2 rounded-2xl p-6 ${onBreak ? "bg-amber-50 border-amber-300" : "bg-emerald-50 border-emerald-300"} border-2`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs uppercase font-semibold tracking-wider text-neutral-500 mb-1">{lang === "de" ? (onBreak ? "In Pause" : "Eingestochen") : (onBreak ? "On break" : "Clocked in")}</p>
                            <p className="font-display text-5xl font-bold tabular-nums" data-testid="tt-elapsed">{elapsed}</p>
                            <p className="text-sm text-neutral-700 mt-2">{active.location_name}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            {statusBadge}
                            {active.start_distance_m !== null && (
                                <p className="text-xs text-neutral-500">{lang === "de" ? "GPS-Abstand" : "GPS dist"}: {Math.round(active.start_distance_m)}m</p>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-2 mt-6">
                        <Button onClick={breakToggle} disabled={busy} variant="outline" className="flex-1" data-testid="tt-break-btn">
                            <PauseIcon className="w-4 h-4 mr-2" /> {onBreak ? (lang === "de" ? "Pause beenden" : "Resume") : (lang === "de" ? "Pause starten" : "Take a break")}
                        </Button>
                        <Button onClick={clockOut} disabled={busy} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="tt-clockout-btn">
                            <Square className="w-4 h-4 mr-2" /> {lang === "de" ? "Ausstechen" : "Clock out"}
                        </Button>
                    </div>
                </div>
                <div className="bg-white rounded-2xl border border-neutral-200 p-4 space-y-2">
                    <p className="text-xs uppercase font-semibold tracking-wider text-neutral-500">{lang === "de" ? "Details" : "Details"}</p>
                    <div className="text-sm space-y-1">
                        <p><strong>{lang === "de" ? "Start" : "Started"}:</strong> {formatDateTime(active.started_at, lang)}</p>
                        <p><strong>{lang === "de" ? "Pausen" : "Breaks"}:</strong> {breaks.length}</p>
                        {active.geofence_violations?.length > 0 && (
                            <p className="text-red-700 font-semibold flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {active.geofence_violations.length} {lang === "de" ? "Verletzungen" : "violations"}</p>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="mt-4 grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2 bg-white rounded-2xl border border-neutral-200 p-6">
                <h2 className="font-display text-xl font-bold mb-1">{lang === "de" ? "Einstechen" : "Clock in"}</h2>
                <p className="text-xs text-neutral-500 mb-4">{lang === "de" ? "Wähle deinen aktuellen Standort" : "Pick your current location"}</p>
                <div className="space-y-2">
                    {options.offices && options.offices.map((o) => (
                        <button key={o.id} onClick={() => setChosen(o.id)} data-testid={`tt-loc-${o.id}`}
                            className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-colors ${chosen === o.id ? "border-blue-600 bg-blue-50" : "border-neutral-200 hover:bg-neutral-50"}`}>
                            <Briefcase className="w-5 h-5 text-neutral-700 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm truncate">{o.name}</p>
                                <p className="text-[10px] text-neutral-500 truncate">{o.display_name || o.address}</p>
                            </div>
                            <span className="text-[10px] text-neutral-400 shrink-0">{o.radius_m}m</span>
                        </button>
                    ))}
                    {options.home_office && (
                        <button onClick={() => setChosen("home_office")} data-testid="tt-loc-home"
                            className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-colors ${chosen === "home_office" ? "border-blue-600 bg-blue-50" : "border-neutral-200 hover:bg-neutral-50"}`}>
                            <Home className="w-5 h-5 text-neutral-700 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm truncate">{lang === "de" ? "Homeoffice" : "Home office"}</p>
                                <p className="text-[10px] text-neutral-500 truncate">{options.home_office.display_name || options.home_office.address}</p>
                            </div>
                            <span className="text-[10px] text-neutral-400 shrink-0">{options.home_office.radius_m}m</span>
                        </button>
                    )}
                    {options.allow_outside && (
                        <button onClick={() => setChosen("outside")} data-testid="tt-loc-outside"
                            className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-colors ${chosen === "outside" ? "border-amber-500 bg-amber-50" : "border-neutral-200 hover:bg-neutral-50"}`}>
                            <MapPin className="w-5 h-5 text-amber-700 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm truncate">{lang === "de" ? "Außendienst / Termin" : "Outside / appointment"}</p>
                                <p className="text-[10px] text-amber-700 truncate">{lang === "de" ? "Benötigt Admin-Genehmigung" : "Requires admin approval"}</p>
                            </div>
                        </button>
                    )}
                    {(!options.offices || options.offices.length === 0) && !options.home_office && !options.allow_outside && (
                        <p className="text-sm text-neutral-500 italic">{lang === "de" ? "Dir wurden noch keine Standorte zugewiesen. Bitte Admin kontaktieren." : "No locations assigned yet. Please contact admin."}</p>
                    )}
                </div>
                <Button onClick={clockIn} disabled={busy || !chosen} className="w-full mt-4 bg-brand-500 hover:bg-brand-600 h-12" data-testid="tt-clockin-btn">
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                    {lang === "de" ? "Einstechen" : "Clock in"}
                </Button>
                {gpsErr && <p className="text-xs text-amber-600 mt-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {gpsErr}</p>}
            </div>
            <div className="bg-white rounded-2xl border border-neutral-200 p-4">
                <p className="text-xs uppercase font-semibold tracking-wider text-neutral-500 mb-2">{lang === "de" ? "Heute" : "Today"}</p>
                <TodayStats lang={lang} />
            </div>
        </div>
    );
}

function TodayStats({ lang }) {
    const [stats, setStats] = useState({ total: 0, count: 0 });
    useEffect(() => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        api.get(`/timetrack/entries?from=${today.toISOString()}`).then((r) => {
            const total = (r.data || []).reduce((s, e) => s + (e.worked_minutes || 0), 0);
            setStats({ total, count: r.data?.length || 0 });
        }).catch(() => {});
    }, []);
    return (
        <div>
            <p className="text-3xl font-display font-bold">{formatDuration(stats.total)}</p>
            <p className="text-xs text-neutral-500">{stats.count} {lang === "de" ? "Einträge" : "entries"}</p>
        </div>
    );
}

// ============= Entries Panel =============
function EntriesPanel({ lang, mode = "me" }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [filterUserId, setFilterUserId] = useState("");
    const [filterFrom, setFilterFrom] = useState("");
    const [filterTo, setFilterTo] = useState("");
    const [retroOpen, setRetroOpen] = useState(false);
    const [editEntry, setEditEntry] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [bulkOpen, setBulkOpen] = useState(false);
    const isAll = mode === "all";

    const toggleSelected = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        if (selected.size === entries.length) setSelected(new Set());
        else setSelected(new Set(entries.map((e) => e.id)));
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const q = new URLSearchParams();
            if (isAll && filterUserId) q.set("user_id", filterUserId);
            if (filterFrom) q.set("from", filterFrom);
            if (filterTo) q.set("to", filterTo);
            const { data } = await api.get(`/timetrack/entries?${q.toString()}`);
            setEntries(data);
        } catch (e) { toast.error(errMsg(e, "Error")); } finally { setLoading(false); }
    }, [isAll, filterUserId, filterFrom, filterTo]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (isAll) api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
    }, [isAll]);

    const downloadCSV = () => {
        const q = new URLSearchParams();
        if (isAll && filterUserId) q.set("user_id", filterUserId);
        if (filterFrom) q.set("from", filterFrom);
        if (filterTo) q.set("to", filterTo);
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/timetrack/entries/export.csv?${q.toString()}`;
        const token = localStorage.getItem("taskflow_token");
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `Zeiterfassung_${new Date().toISOString().slice(0, 10)}.csv`;
                link.click();
                URL.revokeObjectURL(link.href);
            })
            .catch(() => toast.error("CSV download failed"));
    };

    return (
        <div className="mt-4 bg-white rounded-2xl border border-neutral-200 overflow-hidden">
            <div className="p-3 border-b border-neutral-200 space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Einträge" : "Entries"} · {entries.length}</h3>
                    <div className="flex gap-2 flex-wrap">
                        {isAll && selected.size > 0 && (
                            <Button size="sm" onClick={() => setBulkOpen(true)} className="h-8 gap-1 bg-amber-600 hover:bg-amber-700 text-white" data-testid="tt-bulk-edit-btn">
                                <Edit3 className="w-3.5 h-3.5" /> {selected.size} {lang === "de" ? "bearbeiten" : "edit"}
                            </Button>
                        )}
                        {!isAll && (
                            <Button size="sm" onClick={() => setRetroOpen(true)} variant="outline" className="h-8 gap-1" data-testid="tt-retro-btn">
                                <Plus className="w-3.5 h-3.5" /> {lang === "de" ? "Vergessen zu stempeln?" : "Forgot to clock in?"}
                            </Button>
                        )}
                        <Button onClick={load} variant="outline" size="sm" className="h-8"><RefreshCw className="w-3.5 h-3.5" /></Button>
                        <Button onClick={downloadCSV} variant="outline" size="sm" className="h-8 gap-1" data-testid="tt-csv-btn"><Download className="w-3.5 h-3.5" /> CSV</Button>
                    </div>
                </div>
                {/* Filter row */}
                <div className="flex items-center gap-2 flex-wrap text-xs">
                    {isAll && (
                        <select
                            value={filterUserId}
                            onChange={(e) => setFilterUserId(e.target.value)}
                            className="border border-neutral-300 rounded px-2 py-1 text-xs"
                            data-testid="tt-filter-user"
                        >
                            <option value="">{lang === "de" ? "Alle Mitarbeiter" : "All users"}</option>
                            {users.filter((u) => u.role !== "deactivated").map((u) => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                            ))}
                        </select>
                    )}
                    <input
                        type="date"
                        value={filterFrom}
                        onChange={(e) => setFilterFrom(e.target.value ? e.target.value + "T00:00:00.000Z" : "")}
                        className="border border-neutral-300 rounded px-2 py-1 text-xs"
                        data-testid="tt-filter-from"
                        placeholder="Von"
                    />
                    <span className="text-neutral-500">→</span>
                    <input
                        type="date"
                        value={filterTo ? filterTo.slice(0, 10) : ""}
                        onChange={(e) => setFilterTo(e.target.value ? e.target.value + "T23:59:59.999Z" : "")}
                        className="border border-neutral-300 rounded px-2 py-1 text-xs"
                        data-testid="tt-filter-to"
                        placeholder="Bis"
                    />
                    {(filterFrom || filterTo || filterUserId) && (
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-neutral-500" onClick={() => { setFilterUserId(""); setFilterFrom(""); setFilterTo(""); }} data-testid="tt-filter-clear">
                            <XCircle className="w-3 h-3 mr-1" /> {lang === "de" ? "Filter löschen" : "Clear filters"}
                        </Button>
                    )}
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[700px]">
                    <thead className="bg-neutral-50 text-neutral-600 text-[10px] uppercase tracking-wider">
                        <tr>
                            {isAll && (
                                <th className="text-left px-3 py-2 font-semibold w-8">
                                    <input
                                        type="checkbox"
                                        checked={entries.length > 0 && selected.size === entries.length}
                                        onChange={toggleAll}
                                        className="rounded"
                                        data-testid="tt-select-all"
                                    />
                                </th>
                            )}
                            {isAll && <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Mitarbeiter" : "User"}</th>}
                            <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Standort" : "Location"}</th>
                            <th className="text-left px-3 py-2 font-semibold">Start</th>
                            <th className="text-left px-3 py-2 font-semibold">Ende</th>
                            <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Gearbeitet" : "Worked"}</th>
                            <th className="text-left px-3 py-2 font-semibold">Status</th>
                            <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "GPS" : "GPS"}</th>
                            {isAll && <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Aktionen" : "Actions"}</th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                        {loading ? <tr><td colSpan={isAll ? 9 : 6} className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>
                        : entries.length === 0 ? <tr><td colSpan={isAll ? 9 : 6} className="p-6 text-center text-neutral-500">—</td></tr>
                        : entries.map((e) => (
                            <tr key={e.id} className={`hover:bg-neutral-50 ${selected.has(e.id) ? "bg-brand-50" : ""}`} data-testid={`tt-entry-${e.id}`}>
                                {isAll && (
                                    <td className="px-3 py-2">
                                        <input
                                            type="checkbox"
                                            checked={selected.has(e.id)}
                                            onChange={() => toggleSelected(e.id)}
                                            className="rounded"
                                            data-testid={`tt-select-${e.id}`}
                                        />
                                    </td>
                                )}
                                {isAll && <td className="px-3 py-2 font-medium">{e.user_name}</td>}
                                <td className="px-3 py-2">
                                    <div className="flex items-center gap-1.5">
                                        {e.location_type === "home_office" ? <Home className="w-3 h-3" /> : e.location_type === "outside" ? <MapPin className="w-3 h-3 text-amber-600" /> : <Briefcase className="w-3 h-3" />}
                                        <span className="font-medium">{e.location_name}</span>
                                        {e.is_retro && <Badge variant="outline" className="text-[9px] ml-1 border-blue-300 text-blue-700">retro</Badge>}
                                        {e.edited_at && <Badge variant="outline" className="text-[9px] ml-1 border-amber-300 text-amber-700" title={`${e.edited_by_name}: ${e.edit_reason || ""}`}>edit</Badge>}
                                    </div>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(e.started_at, lang)}</td>
                                <td className="px-3 py-2 whitespace-nowrap">{e.ended_at ? formatDateTime(e.ended_at, lang) : <Badge variant="outline" className="text-[10px]">{lang === "de" ? "aktiv" : "active"}</Badge>}</td>
                                <td className="px-3 py-2 font-medium">{formatDuration(e.worked_minutes)}</td>
                                <td className="px-3 py-2">
                                    <Badge className={`text-[10px] ${e.approval_status === "pending" ? "bg-amber-100 text-amber-800 border-amber-300" : e.approval_status === "rejected" ? "bg-red-100 text-red-800 border-red-300" : "bg-emerald-100 text-emerald-800 border-emerald-300"}`}>
                                        {e.approval_status === "pending" ? (lang === "de" ? "ausstehend" : "pending") : e.approval_status === "rejected" ? (lang === "de" ? "abgelehnt" : "rejected") : (lang === "de" ? "genehmigt" : "approved")}
                                    </Badge>
                                </td>
                                <td className="px-3 py-2 text-neutral-600">
                                    {e.start_distance_m !== null && e.start_distance_m !== undefined ? `${Math.round(e.start_distance_m)}m` : "—"}
                                    {(e.geofence_violations?.length || 0) > 0 && <span className="ml-1 text-red-700 font-semibold">+{e.geofence_violations.length}!</span>}
                                </td>
                                {isAll && (
                                    <td className="px-3 py-2">
                                        <Button onClick={() => setEditEntry(e)} variant="ghost" size="sm" className="h-7 px-2" data-testid={`tt-edit-${e.id}`}><Edit3 className="w-3.5 h-3.5" /></Button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {retroOpen && <RetroEntryDialog onClose={() => setRetroOpen(false)} onSaved={() => { setRetroOpen(false); load(); }} lang={lang} />}
            {editEntry && <AdminEditEntryDialog entry={editEntry} onClose={() => setEditEntry(null)} onSaved={() => { setEditEntry(null); load(); }} lang={lang} />}
            {bulkOpen && (
                <BulkEditDialog
                    entryIds={[...selected]}
                    onClose={() => setBulkOpen(false)}
                    onSaved={() => { setBulkOpen(false); setSelected(new Set()); load(); }}
                    lang={lang}
                />
            )}
        </div>
    );
}

// ============= Admin Panel =============
function AdminPanel({ lang }) {
    const [subtab, setSubtab] = useState("locations");
    return (
        <div className="mt-4">
            <Tabs value={subtab} onValueChange={setSubtab}>
                <TabsList>
                    <TabsTrigger value="locations" data-testid="tt-admin-tab-locations"><MapPin className="w-3.5 h-3.5 mr-1.5" />{lang === "de" ? "Standorte" : "Locations"}</TabsTrigger>
                    <TabsTrigger value="users" data-testid="tt-admin-tab-users"><Users className="w-3.5 h-3.5 mr-1.5" />{lang === "de" ? "Mitarbeiter" : "Users"}</TabsTrigger>
                    <TabsTrigger value="schedules" data-testid="tt-admin-tab-schedules"><CalendarDays className="w-3.5 h-3.5 mr-1.5" />{lang === "de" ? "Sollpläne" : "Schedules"}</TabsTrigger>
                    <TabsTrigger value="approvals" data-testid="tt-admin-tab-approvals"><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />{lang === "de" ? "Genehmigungen" : "Approvals"}</TabsTrigger>
                    <TabsTrigger value="all" data-testid="tt-admin-tab-all"><Clock className="w-3.5 h-3.5 mr-1.5" />{lang === "de" ? "Alle Einträge" : "All entries"}</TabsTrigger>
                </TabsList>
                <TabsContent value="locations"><LocationsAdmin lang={lang} /></TabsContent>
                <TabsContent value="users"><UsersAdmin lang={lang} /></TabsContent>
                <TabsContent value="schedules"><SchedulesAdmin lang={lang} /></TabsContent>
                <TabsContent value="approvals"><ApprovalsAdmin lang={lang} /></TabsContent>
                <TabsContent value="all"><AllEntriesAdmin lang={lang} /></TabsContent>
            </Tabs>
        </div>
    );
}

function SchedulesAdmin({ lang }) {
    const [templates, setTemplates] = useState([]);
    const [users, setUsers] = useState([]);
    const [userSettings, setUserSettings] = useState({}); // user_id → settings
    const [editing, setEditing] = useState(null); // template being edited (null = closed)
    const [creating, setCreating] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [t, u] = await Promise.all([
                api.get("/timetrack/schedule-templates"),
                api.get("/users"),
            ]);
            setTemplates(t.data || []);
            setUsers((u.data || []).filter((x) => x.role !== "deactivated"));
            // Load each user's settings to know what's assigned
            const map = {};
            await Promise.all((u.data || []).map(async (usr) => {
                try {
                    const r = await api.get(`/timetrack/users/${usr.id}/settings`);
                    map[usr.id] = r.data || {};
                } catch { map[usr.id] = {}; }
            }));
            setUserSettings(map);
        } catch (e) { toast.error(errMsg(e, "Fehler")); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const delTemplate = async (tid) => {
        if (!window.confirm(lang === "de" ? "Vorlage wirklich löschen? Sie wird auch von allen Mitarbeitern entfernt." : "Delete template? Will be unassigned from all users.")) return;
        try { await api.delete(`/timetrack/schedule-templates/${tid}`); toast.success("OK"); load(); }
        catch (e) { toast.error(errMsg(e, "Error")); }
    };
    const assignTemplate = async (userId, tid) => {
        try {
            await api.put(`/timetrack/users/${userId}/schedule`, { template_id: tid || null });
            toast.success(lang === "de" ? "Zugewiesen" : "Assigned");
            load();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    if (loading) return <div className="p-4"><Loader2 className="w-4 h-4 animate-spin text-neutral-400" /></div>;

    return (
        <div className="space-y-4 mt-2">
            {/* Templates */}
            <div className="bg-white rounded-2xl border border-neutral-200">
                <div className="p-3 border-b border-neutral-200 flex items-center justify-between">
                    <h3 className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Sollplan-Vorlagen" : "Schedule Templates"} · {templates.length}</h3>
                    <Button size="sm" onClick={() => setCreating(true)} data-testid="tt-schedule-new">
                        <Plus className="w-3.5 h-3.5 mr-1" /> {lang === "de" ? "Neue Vorlage" : "New template"}
                    </Button>
                </div>
                {templates.length === 0 ? (
                    <p className="p-4 text-xs text-neutral-500">{lang === "de" ? "Noch keine Sollpläne. Beispiel: Mo-Fr 8h, 7-Tage-Schicht 6h, Teilzeit Mo/Di/Mi 5h." : "No schedules yet."}</p>
                ) : (
                    <div className="divide-y divide-neutral-100">
                        {templates.map((t) => (
                            <div key={t.id} className="p-3 flex items-start gap-3" data-testid={`tt-schedule-tpl-${t.id}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className="font-semibold text-sm">{t.name}</p>
                                        <p className="text-[11px] text-neutral-500">{t.description}</p>
                                    </div>
                                    <SchedulePatternBadges pattern={t.pattern} lang={lang} />
                                </div>
                                <Button size="sm" variant="ghost" onClick={() => setEditing(t)} data-testid={`tt-schedule-edit-${t.id}`}><Edit3 className="w-3.5 h-3.5" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => delTemplate(t.id)} data-testid={`tt-schedule-del-${t.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Per-user assignment */}
            <div className="bg-white rounded-2xl border border-neutral-200">
                <div className="p-3 border-b border-neutral-200">
                    <h3 className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Zuweisung pro Mitarbeiter" : "Per-user assignment"}</h3>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[480px]">
                    <thead className="bg-neutral-50 text-neutral-500">
                        <tr>
                            <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Mitarbeiter" : "User"}</th>
                            <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Aktueller Sollplan" : "Current schedule"}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => {
                            const tid = userSettings[u.id]?.schedule_template_id || "";
                            return (
                                <tr key={u.id} className="border-t border-neutral-100">
                                    <td className="px-3 py-2 font-medium">{u.name} <span className="text-[10px] text-neutral-400">({u.email})</span></td>
                                    <td className="px-3 py-2">
                                        <select
                                            value={tid}
                                            onChange={(e) => assignTemplate(u.id, e.target.value)}
                                            className="border border-neutral-300 rounded px-2 py-1 text-xs w-full max-w-xs"
                                            data-testid={`tt-schedule-assign-${u.id}`}
                                        >
                                            <option value="">— {lang === "de" ? "Kein Sollplan" : "No schedule"} —</option>
                                            {templates.map((t) => (
                                                <option key={t.id} value={t.id}>{t.name}</option>
                                            ))}
                                        </select>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
            </div>

            {(creating || editing) && (
                <ScheduleEditorDialog
                    template={editing}
                    onClose={() => { setCreating(false); setEditing(null); }}
                    onSaved={() => { setCreating(false); setEditing(null); load(); }}
                    lang={lang}
                />
            )}
        </div>
    );
}

function SchedulePatternBadges({ pattern, lang }) {
    const labels = lang === "de" ? ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const total = (pattern || []).reduce((s, x) => s + (x || 0), 0);
    return (
        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
            {(pattern || []).map((h, i) => (
                <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded ${(h || 0) > 0 ? "bg-emerald-100 text-emerald-800" : "bg-neutral-100 text-neutral-400"}`}>
                    {labels[i]}: {h || 0}h
                </span>
            ))}
            <span className="text-[10px] text-neutral-600 ml-1">Σ {total}h/Woche</span>
        </div>
    );
}

function ScheduleEditorDialog({ template, onClose, onSaved, lang }) {
    const [name, setName] = useState(template?.name || "");
    const [description, setDescription] = useState(template?.description || "");
    const [pattern, setPattern] = useState(template?.pattern || [8, 8, 8, 8, 8, 0, 0]);
    const [saving, setSaving] = useState(false);
    const labels = lang === "de" ? ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    const save = async () => {
        if (!name.trim()) { toast.error(lang === "de" ? "Name erforderlich" : "Name required"); return; }
        const total = pattern.reduce((s, x) => s + (parseFloat(x) || 0), 0);
        if (total <= 0) { toast.error(lang === "de" ? "Mindestens 1 Tag mit Stunden > 0" : "At least 1 day > 0"); return; }
        setSaving(true);
        try {
            const body = { name, description, pattern: pattern.map((x) => parseFloat(x) || 0) };
            if (template?.id) await api.put(`/timetrack/schedule-templates/${template.id}`, body);
            else await api.post("/timetrack/schedule-templates", body);
            toast.success(lang === "de" ? "Gespeichert" : "Saved");
            onSaved();
        } catch (e) { toast.error(errMsg(e, "Error")); }
        finally { setSaving(false); }
    };
    const presets = [
        { name: lang === "de" ? "Mo-Fr 8h" : "Mon-Fri 8h", pattern: [8, 8, 8, 8, 8, 0, 0] },
        { name: lang === "de" ? "7-Tage 6h" : "7-day 6h", pattern: [6, 6, 6, 6, 6, 6, 6] },
        { name: lang === "de" ? "Teilzeit 4h" : "Part-time 4h", pattern: [4, 4, 4, 4, 4, 0, 0] },
        { name: lang === "de" ? "Wochenende" : "Weekend", pattern: [0, 0, 0, 0, 0, 8, 8] },
    ];

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full" onClick={(e) => e.stopPropagation()} data-testid="tt-schedule-editor">
                <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <h3 className="font-semibold">{template ? (lang === "de" ? "Sollplan bearbeiten" : "Edit schedule") : (lang === "de" ? "Neuer Sollplan" : "New schedule")}</h3>
                    <Button variant="ghost" size="sm" onClick={onClose}><XCircle className="w-4 h-4" /></Button>
                </header>
                <div className="p-4 space-y-3">
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Name" : "Name"}</label>
                        <input value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" placeholder="z.B. Sales Mo-Fr 8h" data-testid="tt-schedule-name" />
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Beschreibung (optional)" : "Description"}</label>
                        <input value={description} onChange={(e) => setDescription(e.target.value)} className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" />
                    </div>
                    <div>
                        <p className="text-[11px] font-semibold text-neutral-600 mb-2">{lang === "de" ? "Vorlagen" : "Presets"}</p>
                        <div className="flex flex-wrap gap-1">
                            {presets.map((p) => (
                                <button key={p.name} onClick={() => setPattern(p.pattern)} className="text-[10px] bg-neutral-100 hover:bg-neutral-200 px-2 py-1 rounded">
                                    {p.name}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Stunden pro Wochentag" : "Hours per weekday"}</label>
                        <div className="grid grid-cols-7 gap-1">
                            {pattern.map((h, i) => (
                                <div key={i} className="flex flex-col">
                                    <span className="text-[10px] text-center text-neutral-500 mb-0.5">{labels[i]}</span>
                                    <input
                                        type="number"
                                        min="0"
                                        max="24"
                                        step="0.5"
                                        value={h}
                                        onChange={(e) => {
                                            const v = [...pattern];
                                            v[i] = e.target.value;
                                            setPattern(v);
                                        }}
                                        className="border border-neutral-300 rounded px-1 py-1 text-xs text-center"
                                        data-testid={`tt-schedule-day-${i}`}
                                    />
                                </div>
                            ))}
                        </div>
                        <p className="text-[10px] text-neutral-500 mt-2">Σ {pattern.reduce((s, x) => s + (parseFloat(x) || 0), 0)}h / {lang === "de" ? "Woche" : "week"} = ~{(pattern.reduce((s, x) => s + (parseFloat(x) || 0), 0) * 4.33).toFixed(0)}h / {lang === "de" ? "Monat" : "month"}</p>
                    </div>
                </div>
                <footer className="px-4 py-3 border-t border-neutral-200 flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                    <Button onClick={save} disabled={saving} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="tt-schedule-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "de" ? "Speichern" : "Save")}
                    </Button>
                </footer>
            </div>
        </div>
    );
}

function LocationsAdmin({ lang }) {
    const [locs, setLocs] = useState([]);
    const [newOpen, setNewOpen] = useState(false);
    const [edit, setEdit] = useState(null);

    const load = useCallback(async () => {
        const { data } = await api.get("/timetrack/locations");
        setLocs(data);
    }, []);

    useEffect(() => { load(); }, [load]);

    const del = async (id) => {
        if (!window.confirm(lang === "de" ? "Wirklich löschen?" : "Delete?")) return;
        try { await api.delete(`/timetrack/locations/${id}`); load(); toast.success("OK"); } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    return (
        <div className="bg-white rounded-2xl border border-neutral-200 mt-2">
            <div className="p-3 border-b border-neutral-200 flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Standorte" : "Locations"} · {locs.length}</h3>
                <Button onClick={() => setNewOpen(true)} size="sm" className="bg-neutral-900 hover:bg-neutral-800 h-8" data-testid="tt-new-loc-btn"><Plus className="w-3.5 h-3.5 mr-1" />{lang === "de" ? "Neu" : "New"}</Button>
            </div>
            <div className="divide-y divide-neutral-100">
                {locs.length === 0 ? <p className="p-6 text-center text-xs text-neutral-500">—</p>
                : locs.map((l) => (
                    <div key={l.id} className="p-3 flex items-center gap-3" data-testid={`tt-loc-row-${l.id}`}>
                        <MapPin className="w-4 h-4 text-neutral-500" />
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm">{l.name}</p>
                            <p className="text-[10px] text-neutral-500 truncate">{l.display_name || l.address} · {l.lat.toFixed(4)},{l.lng.toFixed(4)} · {lang === "de" ? "Radius" : "radius"}: {l.radius_m}m</p>
                        </div>
                        <Button onClick={() => setEdit(l)} variant="ghost" size="sm" className="h-7 px-2" data-testid={`tt-edit-loc-${l.id}`}><Edit3 className="w-3.5 h-3.5" /></Button>
                        <Button onClick={() => del(l.id)} variant="ghost" size="sm" className="h-7 px-2 text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                ))}
            </div>
            <LocationDialog open={newOpen} onClose={() => setNewOpen(false)} onSaved={() => { setNewOpen(false); load(); }} lang={lang} />
            <LocationDialog open={!!edit} loc={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} lang={lang} />
        </div>
    );
}

function LocationDialog({ open, loc, onClose, onSaved, lang }) {
    const [name, setName] = useState("");
    const [address, setAddress] = useState("");
    const [radius, setRadius] = useState(1000);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (open) {
            setName(loc?.name || "");
            setAddress(loc?.address || "");
            setRadius(loc?.radius_m || 1000);
        }
    }, [open, loc]);
    const save = async () => {
        if (!name.trim() || !address.trim()) return;
        setBusy(true);
        try {
            if (loc?.id) {
                await api.patch(`/timetrack/locations/${loc.id}`, { name, address, radius_m: Number(radius) });
                toast.success("OK");
            } else {
                await api.post("/timetrack/locations", { name, address, radius_m: Number(radius) });
                toast.success(lang === "de" ? "Standort angelegt (Geocoding via OSM)" : "Created");
            }
            onSaved();
        } catch (e) { toast.error(errMsg(e, "Error")); } finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="tt-loc-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{loc ? (lang === "de" ? "Standort bearbeiten" : "Edit location") : (lang === "de" ? "Neuer Standort" : "New location")}</DialogTitle>
                    <DialogDescription className="text-xs">{lang === "de" ? "Adresse wird via OpenStreetMap in GPS-Koordinaten umgewandelt." : "Address is geocoded via OpenStreetMap."}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div><Label className="text-xs">{lang === "de" ? "Name" : "Name"}</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Büro Düsseldorf" className="mt-1" data-testid="tt-loc-name" /></div>
                    <div><Label className="text-xs">{lang === "de" ? "Adresse" : "Address"}</Label><Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Königsallee 27, 40212 Düsseldorf" className="mt-1" data-testid="tt-loc-address" /></div>
                    <div><Label className="text-xs">{lang === "de" ? "Radius (Meter)" : "Radius (m)"}</Label><Input type="number" value={radius} onChange={(e) => setRadius(e.target.value)} className="mt-1" data-testid="tt-loc-radius" /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                    <Button onClick={save} disabled={busy || !name.trim() || !address.trim()} className="bg-neutral-900 hover:bg-neutral-800" data-testid="tt-loc-save">
                        {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                        {lang === "de" ? "Speichern" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function UsersAdmin({ lang }) {
    const [users, setUsers] = useState([]);
    const [locs, setLocs] = useState([]);
    const [editUser, setEditUser] = useState(null);

    const load = useCallback(async () => {
        const [u, l] = await Promise.all([api.get("/users"), api.get("/timetrack/locations")]);
        setUsers(u.data || []);
        setLocs(l.data || []);
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="bg-white rounded-2xl border border-neutral-200 mt-2">
            <div className="p-3 border-b border-neutral-200 flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Mitarbeiter-Zuordnung" : "User Assignment"}</h3>
                <Button onClick={load} variant="ghost" size="sm" className="h-7 px-2"><RefreshCw className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="divide-y divide-neutral-100">
                {users.map((u) => (
                    <div key={u.id} className="p-3 flex items-center justify-between" data-testid={`tt-user-row-${u.id}`}>
                        <div>
                            <p className="font-semibold text-sm">{u.name}</p>
                            <p className="text-[10px] text-neutral-500">{u.email}</p>
                        </div>
                        <Button onClick={() => setEditUser(u)} variant="outline" size="sm" className="h-8" data-testid={`tt-edit-user-${u.id}`}><Edit3 className="w-3.5 h-3.5 mr-1" />{lang === "de" ? "Bearbeiten" : "Edit"}</Button>
                    </div>
                ))}
            </div>
            {editUser && <UserSettingsDialog user={editUser} locs={locs} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} lang={lang} />}
        </div>
    );
}

function UserSettingsDialog({ user, locs, onClose, onSaved, lang }) {
    const [allowedIds, setAllowedIds] = useState([]);
    const [allowOutside, setAllowOutside] = useState(true);
    const [homeAddress, setHomeAddress] = useState("");
    const [homeRadius, setHomeRadius] = useState(1000);
    const [hasHome, setHasHome] = useState(false);
    const [weeklyHours, setWeeklyHours] = useState(40);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!user) return;
        api.get(`/timetrack/users/${user.id}/settings`).then((r) => {
            setAllowedIds(r.data.allowed_location_ids || []);
            setAllowOutside(r.data.allow_outside ?? true);
            setHomeAddress(r.data.home_office?.address || "");
            setHomeRadius(r.data.home_office?.radius_m || 1000);
            setHasHome(!!r.data.home_office);
            setWeeklyHours(r.data.weekly_hours || 40);
        });
    }, [user]);

    const save = async () => {
        setBusy(true);
        try {
            await api.post(`/timetrack/users/${user.id}/locations`, {
                location_ids: allowedIds,
                allow_outside: allowOutside,
                weekly_hours: Number(weeklyHours),
            });
            if (homeAddress.trim()) {
                await api.post(`/timetrack/users/${user.id}/home-office`, { address: homeAddress, radius_m: Number(homeRadius) });
            } else if (hasHome) {
                await api.delete(`/timetrack/users/${user.id}/home-office`);
            }
            toast.success("OK");
            onSaved();
        } catch (e) { toast.error(errMsg(e, "Error")); } finally { setBusy(false); }
    };

    return (
        <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="tt-user-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{user.name}</DialogTitle>
                    <DialogDescription className="text-xs">{lang === "de" ? "Standort-Zuordnung & Homeoffice" : "Locations & home office"}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Erlaubte Firmen-Standorte" : "Allowed offices"}</Label>
                        <div className="mt-1.5 space-y-1.5">
                            {locs.length === 0 ? <p className="text-xs text-neutral-500 italic">{lang === "de" ? "Keine Standorte angelegt" : "No locations defined"}</p>
                            : locs.map((l) => {
                                const active = allowedIds.includes(l.id);
                                return (
                                    <button key={l.id} type="button" onClick={() => setAllowedIds((p) => p.includes(l.id) ? p.filter((x) => x !== l.id) : [...p, l.id])}
                                        className={`w-full text-left p-2 rounded-md border ${active ? "border-blue-600 bg-blue-50" : "border-neutral-200"}`} data-testid={`tt-toggle-loc-${l.id}`}>
                                        <p className="text-sm font-medium">{l.name}</p>
                                        <p className="text-[10px] text-neutral-500 truncate">{l.address}</p>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={allowOutside} onChange={(e) => setAllowOutside(e.target.checked)} data-testid="tt-allow-outside" />
                            <MapPin className="w-3.5 h-3.5" /> {lang === "de" ? "Außendienst erlaubt (Admin-Genehmigung nötig)" : "Allow outside (needs approval)"}
                        </label>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Soll-Stunden pro Woche" : "Target hours per week"}</Label>
                        <div className="flex items-center gap-2 mt-1.5">
                            <Input type="number" step="0.5" min="0" max="60" value={weeklyHours} onChange={(e) => setWeeklyHours(e.target.value)} className="w-28" data-testid="tt-weekly-hours" />
                            <span className="text-xs text-neutral-500">{lang === "de" ? "Std. (Standard: 40)" : "hrs (default: 40)"}</span>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Homeoffice-Adresse" : "Home office address"}</Label>
                        <Input value={homeAddress} onChange={(e) => setHomeAddress(e.target.value)} placeholder="Musterstraße 5, 40000 Stadt" className="mt-1.5" data-testid="tt-home-address" />
                        <div className="flex items-center gap-2 mt-1.5">
                            <Label className="text-xs">{lang === "de" ? "Radius" : "Radius"}:</Label>
                            <Input type="number" value={homeRadius} onChange={(e) => setHomeRadius(e.target.value)} className="w-24 h-7 text-xs" data-testid="tt-home-radius" />
                            <span className="text-xs text-neutral-500">m</span>
                        </div>
                        <p className="text-[10px] text-neutral-500 mt-1">{lang === "de" ? "Leer lassen, um zu entfernen. Adresse wird via OSM geocoded." : "Empty to remove. Address is geocoded."}</p>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                    <Button onClick={save} disabled={busy} className="bg-neutral-900 hover:bg-neutral-800" data-testid="tt-user-save">
                        {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                        {lang === "de" ? "Speichern" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ApprovalsAdmin({ lang }) {
    const [entries, setEntries] = useState([]);
    const load = useCallback(async () => {
        const { data } = await api.get("/timetrack/entries?status=pending");
        setEntries(data);
    }, []);
    useEffect(() => { load(); }, [load]);

    const decide = async (id, decision) => {
        try {
            if (decision === "approve") await api.post(`/timetrack/entries/${id}/approve`);
            else await api.post(`/timetrack/entries/${id}/reject`, { reason: prompt(lang === "de" ? "Grund:" : "Reason:") || "" });
            toast.success("OK");
            load();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    return (
        <div className="bg-white rounded-2xl border border-neutral-200 mt-2 divide-y divide-neutral-100">
            <div className="p-3 flex items-center justify-between">
                <h3 className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Wartende Genehmigungen" : "Pending approvals"} · {entries.length}</h3>
                <Button onClick={load} variant="ghost" size="sm" className="h-7 px-2"><RefreshCw className="w-3.5 h-3.5" /></Button>
            </div>
            {entries.length === 0 ? <p className="p-6 text-center text-xs text-neutral-500">—</p>
            : entries.map((e) => (
                <div key={e.id} className="p-3" data-testid={`tt-pending-${e.id}`}>
                    <div className="flex items-start justify-between mb-2">
                        <div>
                            <p className="font-semibold text-sm">{e.user_name}</p>
                            <p className="text-[10px] text-neutral-500">{formatDateTime(e.started_at, lang)} → {e.ended_at ? formatDateTime(e.ended_at, lang) : (lang === "de" ? "noch aktiv" : "still active")}</p>
                            <p className="text-xs mt-1 flex items-center gap-1">
                                {e.location_type === "outside" ? <MapPin className="w-3 h-3 text-amber-600" /> : e.location_type === "home_office" ? <Home className="w-3 h-3" /> : <Briefcase className="w-3 h-3" />}
                                <span className="font-medium">{e.location_name}</span>
                                {e.start_distance_m !== null && e.start_distance_m !== undefined && (
                                    <span className="text-amber-700 ml-1">({Math.round(e.start_distance_m)}m {lang === "de" ? "entfernt" : "away"})</span>
                                )}
                            </p>
                        </div>
                        <div className="flex gap-1">
                            <Button onClick={() => decide(e.id, "approve")} size="sm" className="bg-brand-500 hover:bg-brand-600 h-8" data-testid={`tt-approve-${e.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />{lang === "de" ? "Genehmigen" : "Approve"}</Button>
                            <Button onClick={() => decide(e.id, "reject")} size="sm" variant="destructive" className="h-8" data-testid={`tt-reject-${e.id}`}><XCircle className="w-3.5 h-3.5 mr-1" />{lang === "de" ? "Ablehnen" : "Reject"}</Button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function AllEntriesAdmin({ lang }) {
    return <EntriesPanel lang={lang} mode="all" />;
}

// ============= Bulk Edit Dialog =============
function BulkEditDialog({ entryIds, onClose, onSaved, lang }) {
    const [setBreak, setSetBreak] = useState("");
    const [deltaBreak, setDeltaBreak] = useState("");
    const [shift, setShift] = useState("");
    const [approval, setApproval] = useState("");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!reason.trim()) { toast.error(lang === "de" ? "Bitte Grund angeben (Audit-Trail)" : "Reason required"); return; }
        const body = { entry_ids: entryIds, reason };
        if (setBreak !== "") body.set_break_minutes = parseInt(setBreak, 10);
        if (deltaBreak !== "") body.break_minutes_delta = parseInt(deltaBreak, 10);
        if (shift !== "") body.shift_minutes = parseInt(shift, 10);
        if (approval) body.set_approval_status = approval;
        const hasChange = body.set_break_minutes !== undefined || body.break_minutes_delta !== undefined || body.shift_minutes !== undefined || body.set_approval_status;
        if (!hasChange) { toast.error(lang === "de" ? "Mindestens eine Änderung wählen" : "Pick at least one change"); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/timetrack/entries/bulk-edit", body);
            toast.success(lang === "de" ? `${data.affected} Einträge geändert` : `${data.affected} entries updated`);
            onSaved();
        } catch (e) { toast.error(errMsg(e, "Error")); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="tt-bulk-dialog">
                <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <div>
                        <h3 className="font-semibold">{lang === "de" ? "Mehrfach-Bearbeitung" : "Bulk Edit"}</h3>
                        <p className="text-[11px] text-neutral-500">{entryIds.length} {lang === "de" ? "Einträge ausgewählt" : "entries selected"}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose}><XCircle className="w-4 h-4" /></Button>
                </header>
                <div className="p-4 space-y-3">
                    <p className="text-[11px] text-neutral-500 bg-amber-50 border border-amber-200 rounded p-2">
                        ⚠️ {lang === "de" ? "Diese Änderung wird auf ALLE ausgewählten Einträge angewendet. Im Audit-Log nachverfolgbar." : "This change applies to ALL selected entries. Audit-logged."}
                    </p>
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Pause auf festen Wert setzen (min)" : "Set break (min)"}</label>
                        <input type="number" min="0" value={setBreak} onChange={(e) => setSetBreak(e.target.value)} placeholder="z.B. 30" className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="tt-bulk-set-break" />
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "ODER Pause um Wert ändern (+/-)" : "OR adjust break by"}</label>
                        <input type="number" value={deltaBreak} onChange={(e) => setDeltaBreak(e.target.value)} placeholder="z.B. -10 oder +15" className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="tt-bulk-delta-break" />
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Start + Ende verschieben (Minuten, ±)" : "Shift start+end by (min, ±)"}</label>
                        <input type="number" value={shift} onChange={(e) => setShift(e.target.value)} placeholder="z.B. -30 oder +60" className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="tt-bulk-shift" />
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Status setzen" : "Set status"}</label>
                        <select value={approval} onChange={(e) => setApproval(e.target.value)} className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="tt-bulk-approval">
                            <option value="">—</option>
                            <option value="approved">{lang === "de" ? "Genehmigt" : "Approved"}</option>
                            <option value="rejected">{lang === "de" ? "Abgelehnt" : "Rejected"}</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Grund (Audit-Pflicht)" : "Reason (audit)"} *</label>
                        <input value={reason} onChange={(e) => setReason(e.target.value)} className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" placeholder={lang === "de" ? "z.B. Monatskorrektur Lohn" : "e.g. monthly payroll correction"} data-testid="tt-bulk-reason" />
                    </div>
                </div>
                <footer className="px-4 py-3 border-t border-neutral-200 flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                    <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="tt-bulk-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "de" ? `${entryIds.length} ändern` : `Update ${entryIds.length}`)}
                    </Button>
                </footer>
            </div>
        </div>
    );
}

// ============= Retroactive Entry Dialog =============
function RetroEntryDialog({ onClose, onSaved, lang }) {
    const [locations, setLocations] = useState([]);
    const [hasHome, setHasHome] = useState(false);
    const [allowOutside, setAllowOutside] = useState(false);
    const today = new Date().toISOString().slice(0, 10);
    const [date, setDate] = useState(today);
    const [startTime, setStartTime] = useState("08:00");
    const [endTime, setEndTime] = useState("17:00");
    const [breakMin, setBreakMin] = useState(30);
    const [locationId, setLocationId] = useState("");
    const [locationType, setLocationType] = useState("office");
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [loc, my] = await Promise.all([api.get("/timetrack/locations/mine"), api.get("/auth/me")]);
                setLocations(loc.data || []);
                const settingsRes = await api.get(`/timetrack/users/${my.data.id}/settings`);
                setHasHome(!!settingsRes.data?.home_office);
                setAllowOutside(!!settingsRes.data?.allow_outside);
                if (loc.data?.length > 0) {
                    setLocationId(loc.data[0].id);
                    setLocationType("office");
                }
            } catch { /* ignore */ }
        })();
    }, []);

    const save = async () => {
        if (!locationId) { toast.error(lang === "de" ? "Standort wählen" : "Pick a location"); return; }
        if (!reason.trim()) { toast.error(lang === "de" ? "Bitte Grund angeben" : "Reason required"); return; }
        const startISO = new Date(`${date}T${startTime}:00`).toISOString();
        const endISO = new Date(`${date}T${endTime}:00`).toISOString();
        if (endISO <= startISO) { toast.error(lang === "de" ? "Ende muss nach Start liegen" : "End after start"); return; }
        setSaving(true);
        try {
            await api.post("/timetrack/entries/retro", {
                location_id: locationId,
                location_type: locationType,
                started_at: startISO,
                ended_at: endISO,
                break_minutes: parseInt(breakMin, 10) || 0,
                reason,
                notes,
            });
            toast.success(lang === "de" ? "Nachträglicher Stempel gesendet — Admin prüft." : "Retroactive entry sent for approval.");
            onSaved();
        } catch (e) { toast.error(errMsg(e, "Error")); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="tt-retro-dialog">
                <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <div>
                        <h3 className="font-semibold">{lang === "de" ? "Vergessen zu stempeln?" : "Forgot to clock in?"}</h3>
                        <p className="text-[11px] text-neutral-500">{lang === "de" ? "Nachträglicher Eintrag — wird vom Admin geprüft" : "Retroactive entry — needs admin approval"}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose}><XCircle className="w-4 h-4" /></Button>
                </header>
                <div className="p-4 space-y-3">
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Datum" : "Date"}</label>
                        <input type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)} className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="tt-retro-date" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        <div>
                            <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Start" : "Start"}</label>
                            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="tt-retro-start" />
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Ende" : "End"}</label>
                            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="tt-retro-end" />
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Pause (min)" : "Break (min)"}</label>
                            <input type="number" min="0" max="480" step="5" value={breakMin} onChange={(e) => setBreakMin(e.target.value)} className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="tt-retro-break" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Standort" : "Location"}</label>
                        <select
                            value={locationType === "office" ? locationId : locationType}
                            onChange={(e) => {
                                if (e.target.value === "home_office") { setLocationType("home_office"); setLocationId("home_office"); }
                                else if (e.target.value === "outside") { setLocationType("outside"); setLocationId("outside"); }
                                else { setLocationType("office"); setLocationId(e.target.value); }
                            }}
                            className="w-full border border-neutral-300 rounded px-3 py-2 text-sm"
                            data-testid="tt-retro-location"
                        >
                            {locations.map((l) => <option key={l.id} value={l.id}>🏢 {l.name}</option>)}
                            {hasHome && <option value="home_office">🏠 Home Office</option>}
                            {allowOutside && <option value="outside">📍 {lang === "de" ? "Außendienst" : "Outside"}</option>}
                        </select>
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Grund (Pflicht)" : "Reason"} *</label>
                        <input value={reason} onChange={(e) => setReason(e.target.value)} className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" placeholder={lang === "de" ? "z.B. Handy ohne Akku, war im Termin" : "e.g. Phone died, was in meeting"} data-testid="tt-retro-reason" />
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Notizen (optional)" : "Notes"}</label>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-neutral-300 rounded px-3 py-2 text-sm h-16" />
                    </div>
                </div>
                <footer className="px-4 py-3 border-t border-neutral-200 flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                    <Button onClick={save} disabled={saving} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="tt-retro-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "de" ? "Antrag senden" : "Submit")}
                    </Button>
                </footer>
            </div>
        </div>
    );
}

// ============= Admin Edit Entry Dialog =============
function AdminEditEntryDialog({ entry, onClose, onSaved, lang }) {
    const isoToLocal = (iso) => iso ? new Date(iso).toISOString().slice(0, 16) : "";
    const [startedAt, setStartedAt] = useState(isoToLocal(entry.started_at));
    const [endedAt, setEndedAt] = useState(isoToLocal(entry.ended_at));
    const [breakMin, setBreakMin] = useState(entry.break_minutes || 0);
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState(entry.notes || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!reason.trim()) { toast.error(lang === "de" ? "Bitte Grund angeben (Audit-Trail)" : "Reason required"); return; }
        setSaving(true);
        try {
            await api.put(`/timetrack/entries/${entry.id}`, {
                started_at: new Date(startedAt).toISOString(),
                ended_at: endedAt ? new Date(endedAt).toISOString() : null,
                break_minutes: parseInt(breakMin, 10) || 0,
                notes,
                reason,
            });
            toast.success(lang === "de" ? "Eintrag geändert" : "Entry updated");
            onSaved();
        } catch (e) { toast.error(errMsg(e, "Error")); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="tt-edit-dialog">
                <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
                    <div>
                        <h3 className="font-semibold">{lang === "de" ? "Eintrag bearbeiten (Admin)" : "Edit entry (Admin)"}</h3>
                        <p className="text-[11px] text-neutral-500">{entry.user_name} · {entry.location_name}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose}><XCircle className="w-4 h-4" /></Button>
                </header>
                <div className="p-4 space-y-3">
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">Start</label>
                        <input type="datetime-local" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="tt-edit-start" />
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">Ende</label>
                        <input type="datetime-local" value={endedAt} onChange={(e) => setEndedAt(e.target.value)} className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="tt-edit-end" />
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Pausen (min)" : "Break (min)"}</label>
                        <input type="number" min="0" value={breakMin} onChange={(e) => setBreakMin(e.target.value)} className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" data-testid="tt-edit-break" />
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Notizen" : "Notes"}</label>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-neutral-300 rounded px-3 py-2 text-sm h-16" />
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-neutral-600 mb-1">{lang === "de" ? "Grund der Änderung (Audit)" : "Edit reason (audit)"} *</label>
                        <input value={reason} onChange={(e) => setReason(e.target.value)} className="w-full border border-neutral-300 rounded px-3 py-2 text-sm" placeholder={lang === "de" ? "z.B. Pause vergessen einzutragen" : "e.g. Forgot to log break"} data-testid="tt-edit-reason" />
                    </div>
                </div>
                <footer className="px-4 py-3 border-t border-neutral-200 flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                    <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="tt-edit-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "de" ? "Speichern" : "Save")}
                    </Button>
                </footer>
            </div>
        </div>
    );
}

// ============= Statistics Panel =============
function StatsPanel({ lang, isPriv, userId }) {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [stats, setStats] = useState(null);
    const [team, setTeam] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(userId);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isPriv) api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
    }, [isPriv]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const targetId = selectedUserId || userId;
            const q = `year=${year}&month=${month}${targetId && targetId !== userId ? `&user_id=${targetId}` : ""}`;
            const { data } = await api.get(`/timetrack/stats?${q}`);
            setStats(data);
            if (isPriv) {
                const { data: tdata } = await api.get(`/timetrack/stats/team?year=${year}&month=${month}`);
                setTeam(tdata);
            }
        } catch (e) { toast.error(errMsg(e, "Error")); } finally { setLoading(false); }
    }, [year, month, selectedUserId, userId, isPriv]);

    useEffect(() => { load(); }, [load]);

    const downloadPdf = (uid) => {
        const targetId = uid || selectedUserId;
        const q = `year=${year}&month=${month}${targetId && targetId !== userId ? `&user_id=${targetId}` : ""}`;
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/timetrack/stats/export.pdf?${q}`;
        // Authenticated download
        const token = localStorage.getItem("taskflow_token");
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                const month_names = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
                link.download = `Zeiterfassung_${month_names[month - 1]}_${year}.pdf`;
                link.click();
                URL.revokeObjectURL(link.href);
            })
            .catch(() => toast.error("PDF download failed"));
    };

    const monthNames = lang === "de"
        ? ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"]
        : ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const fmt = (mins) => `${Math.floor((mins || 0) / 60)}h ${(mins || 0) % 60}m`;

    return (
        <div className="mt-4 space-y-4">
            <div className="bg-white rounded-2xl border border-neutral-200 p-3 flex items-center gap-2 flex-wrap" data-testid="tt-stats-controls">
                <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                    <SelectTrigger className="w-36 h-9" data-testid="tt-stats-month"><SelectValue /></SelectTrigger>
                    <SelectContent>{monthNames.map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                    <SelectTrigger className="w-24 h-9" data-testid="tt-stats-year"><SelectValue /></SelectTrigger>
                    <SelectContent>{[2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
                {isPriv && users.length > 0 && (
                    <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                        <SelectTrigger className="w-44 h-9" data-testid="tt-stats-user"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                )}
                <Button onClick={() => load()} variant="outline" size="sm" className="h-9 ml-auto" data-testid="tt-stats-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
                <Button onClick={() => downloadPdf()} size="sm" className="h-9 bg-red-600 hover:bg-red-700" data-testid="tt-stats-pdf-btn"><Download className="w-3.5 h-3.5 mr-1" />PDF</Button>
                {isPriv && (
                    <Button
                        onClick={() => {
                            const token = localStorage.getItem("taskflow_token");
                            const url = `${process.env.REACT_APP_BACKEND_URL}/api/timetrack/stats/team-export.csv?year=${year}&month=${month}`;
                            fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                                .then((r) => r.blob())
                                .then((blob) => {
                                    const link = document.createElement("a");
                                    link.href = URL.createObjectURL(blob);
                                    link.download = `Team-Zeiterfassung_${monthNames[month - 1]}_${year}.csv`;
                                    link.click();
                                    URL.revokeObjectURL(link.href);
                                })
                                .catch(() => toast.error("CSV download failed"));
                        }}
                        size="sm"
                        className="h-9 bg-brand-500 hover:bg-brand-600"
                        data-testid="tt-team-csv-btn"
                    >
                        <Download className="w-3.5 h-3.5 mr-1" /> Team-CSV
                    </Button>
                )}
            </div>

            {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>
            : stats && (
                <>
                    {/* Soll/Ist Summary Cards */}
                    <div className="grid md:grid-cols-4 gap-3">
                        <div className="bg-white rounded-xl border border-neutral-200 p-4" data-testid="tt-stats-target">
                            <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{lang === "de" ? "Soll" : "Target"}</p>
                            <p className="text-2xl font-display font-bold mt-1">{fmt(stats.target_minutes)}</p>
                            <p className="text-[10px] text-neutral-500 mt-0.5">{stats.weekly_hours}h/Wo · {stats.working_days} {lang === "de" ? "AT" : "WD"}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-neutral-200 p-4" data-testid="tt-stats-actual">
                            <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{lang === "de" ? "Ist" : "Actual"}</p>
                            <p className="text-2xl font-display font-bold mt-1">{fmt(stats.actual_minutes)}</p>
                            <p className="text-[10px] text-neutral-500 mt-0.5">{stats.entries_count} {lang === "de" ? "Einträge" : "entries"}</p>
                        </div>
                        <div className={`rounded-xl border p-4 ${stats.diff_minutes >= 0 ? "bg-emerald-50 border-emerald-300" : "bg-red-50 border-red-300"}`} data-testid="tt-stats-diff">
                            <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold flex items-center gap-1">
                                {stats.diff_minutes >= 0 ? <TrendingUp className="w-3 h-3 text-emerald-700" /> : <TrendingDown className="w-3 h-3 text-red-700" />}
                                {lang === "de" ? "Differenz" : "Diff"}
                            </p>
                            <p className={`text-2xl font-display font-bold mt-1 ${stats.diff_minutes >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                                {stats.diff_minutes >= 0 ? "+" : "-"}{fmt(Math.abs(stats.diff_minutes))}
                            </p>
                            <p className="text-[10px] text-neutral-500 mt-0.5">{stats.diff_pct}% {lang === "de" ? "des Solls" : "of target"}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-neutral-200 p-4">
                            <p className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{lang === "de" ? "Pausen" : "Breaks"}</p>
                            <p className="text-2xl font-display font-bold mt-1">{fmt(stats.break_minutes)}</p>
                            {stats.violations > 0 && (
                                <p className="text-[10px] text-red-700 font-semibold mt-0.5 flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> {stats.violations} {lang === "de" ? "Verletzungen" : "violations"}</p>
                            )}
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="bg-white rounded-xl border border-neutral-200 p-4">
                        <div className="flex justify-between text-xs mb-2">
                            <span className="font-semibold">{lang === "de" ? "Fortschritt zum Soll" : "Progress to target"}</span>
                            <span className="text-neutral-500">{fmt(stats.actual_minutes)} / {fmt(stats.target_minutes)}</span>
                        </div>
                        <div className="w-full h-3 bg-neutral-100 rounded-full overflow-hidden">
                            <div
                                className={`h-full transition-all ${stats.diff_pct >= 100 ? "bg-emerald-500" : stats.diff_pct >= 80 ? "bg-blue-500" : "bg-amber-500"}`}
                                style={{ width: `${Math.min(100, stats.diff_pct)}%` }}
                            />
                        </div>
                    </div>

                    {/* Per-Location Breakdown */}
                    {stats.by_location && stats.by_location.length > 0 && (
                        <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                            <h4 className="text-xs font-semibold uppercase tracking-wider p-3 border-b border-neutral-200">{lang === "de" ? "Nach Standort" : "By location"}</h4>
                            <div className="divide-y divide-neutral-100">
                                {stats.by_location.map((row) => {
                                    const pct = stats.actual_minutes ? (row.minutes / stats.actual_minutes * 100) : 0;
                                    return (
                                        <div key={row.name} className="p-3">
                                            <div className="flex justify-between text-sm">
                                                <span className="font-medium">{row.name}</span>
                                                <span className="text-neutral-600">{fmt(row.minutes)} · {pct.toFixed(1)}%</span>
                                            </div>
                                            <div className="w-full h-1.5 bg-neutral-100 rounded-full mt-1 overflow-hidden">
                                                <div className="h-full bg-blue-600" style={{ width: `${pct}%` }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Per-Day Breakdown */}
                    {stats.by_day && stats.by_day.length > 0 && (
                        <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                            <h4 className="text-xs font-semibold uppercase tracking-wider p-3 border-b border-neutral-200">{lang === "de" ? "Tagesübersicht" : "Daily breakdown"}</h4>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead className="bg-neutral-50 text-neutral-500 text-[10px] uppercase tracking-wider">
                                        <tr><th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Datum" : "Date"}</th><th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Wochentag" : "Weekday"}</th><th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Stunden" : "Hours"}</th></tr>
                                    </thead>
                                    <tbody className="divide-y divide-neutral-100">
                                        {stats.by_day.map((row) => {
                                            const d = new Date(row.date);
                                            const wd = lang === "de" ? ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][d.getDay()] : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];
                                            return (
                                                <tr key={row.date}>
                                                    <td className="px-3 py-1.5">{d.toLocaleDateString(LOCALES[lang])}</td>
                                                    <td className="px-3 py-1.5 text-neutral-500">{wd}</td>
                                                    <td className="px-3 py-1.5 font-medium">{fmt(row.minutes)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Team overview (admin) */}
                    {isPriv && team.length > 0 && (
                        <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                            <h4 className="text-xs font-semibold uppercase tracking-wider p-3 border-b border-neutral-200">{lang === "de" ? "Team-Übersicht" : "Team overview"} · {monthNames[month - 1]} {year}</h4>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs min-w-[640px]">
                                    <thead className="bg-neutral-50 text-neutral-500 text-[10px] uppercase tracking-wider">
                                        <tr>
                                            <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Mitarbeiter" : "User"}</th>
                                            <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Sollplan" : "Schedule"}</th>
                                            <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Soll" : "Target"}</th>
                                            <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Ist" : "Actual"}</th>
                                            <th className="text-left px-3 py-2 font-semibold">{lang === "de" ? "Differenz" : "Diff"}</th>
                                            <th className="text-left px-3 py-2 font-semibold">%</th>
                                            <th className="text-left px-3 py-2 font-semibold">PDF</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-neutral-100">
                                        {team.map((s) => (
                                            <tr key={s.user_id} className="hover:bg-neutral-50" data-testid={`tt-team-row-${s.user_id}`}>
                                                <td className="px-3 py-2"><button onClick={() => setSelectedUserId(s.user_id)} className="font-medium hover:underline">{s.user_name}</button></td>
                                                <td className="px-3 py-2 text-[11px] text-neutral-600">{s.schedule_template_name || "—"}</td>
                                                <td className="px-3 py-2">{fmt(s.target_minutes)}</td>
                                                <td className="px-3 py-2 font-medium">{fmt(s.actual_minutes)}</td>
                                                <td className={`px-3 py-2 font-semibold ${s.diff_minutes >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                                                    {s.diff_minutes >= 0 ? "+" : "-"}{fmt(Math.abs(s.diff_minutes))}
                                                </td>
                                                <td className="px-3 py-2">{s.diff_pct}%</td>
                                                <td className="px-3 py-2">
                                                    <Button onClick={() => downloadPdf(s.user_id)} variant="ghost" size="sm" className="h-7 px-2" data-testid={`tt-team-pdf-${s.user_id}`}><Download className="w-3.5 h-3.5" /></Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
